// Tracking → At-risk tab.
//
// Lists in-flight orders the system believes are overdue. Detection
// runs at read time:
//   1. Compute lane p90 transit baselines from delivered orders in the
//      window. Lane = (destination_state, service_class).
//   2. For each in-flight order, expected_by = shipped_date + lane_p90.
//   3. If today > expected_by, flag it. days_overdue = today - expected_by.
//
// Severity tiers (operator triage cue):
//   mild     — 1-2d overdue
//   moderate — 3-5d overdue
//   severe   — 6d+ overdue
//
// International orders are excluded by default — AP can't surface
// destination-carrier scans for them, so they'd all flood the list as
// false-positive "overdue". Toggle the chip to include for manual
// follow-up.

import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import {
  Loader2, AlertTriangle, OctagonAlert, RefreshCw, Globe,
} from 'lucide-react'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { getTrackingAtRisk } from '../../lib/api'
import OrderDetailDrawer from './OrderDetailDrawer'

function formatNum(v) { return v == null ? '—' : new Intl.NumberFormat('en-AU').format(v) }
function timeAgo(iso) {
  if (!iso) return '—'
  try { return formatDistanceToNow(parseISO(iso), { addSuffix: true }) } catch { return '—' }
}

const SEVERITY_META = {
  mild:     { color: 'bg-amber-50 text-amber-700 border-amber-200',   label: 'Mild'     },
  moderate: { color: 'bg-orange-50 text-orange-700 border-orange-200', label: 'Moderate' },
  severe:   { color: 'bg-red-50 text-red-700 border-red-200',          label: 'Severe'   },
}

function SeverityPill({ severity }) {
  const m = SEVERITY_META[severity] || SEVERITY_META.mild
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] font-medium border ${m.color}`}>
      {severity === 'severe' && <OctagonAlert size={10} />}
      {severity === 'moderate' && <AlertTriangle size={10} />}
      {m.label}
    </span>
  )
}

export default function TrackingAtRiskTab() {
  const qc = useQueryClient()
  const [days, setDays] = useState(30)
  const [includeIntl, setIncludeIntl] = useState(false)
  const [params] = useSearchParams()
  const clientId = params.get('client_id') || undefined
  const [drawerRow, setDrawerRow] = useState(null)

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['tracking', 'at-risk', days, clientId, includeIntl],
    queryFn:  () => getTrackingAtRisk({
      days,
      client_id:             clientId,
      include_international: includeIntl,
    }),
    refetchInterval: 60_000,
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 gap-2 text-slate-400">
        <Loader2 size={16} className="animate-spin" /> Computing at-risk shipments…
      </div>
    )
  }

  const flags = data?.flags || []
  const stats = data?.stats || {}

  return (
    <div className="space-y-4">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="card p-3">
          <p className="text-[10px] uppercase tracking-wider font-medium text-slate-400">In-flight</p>
          <p className="text-xl font-bold text-slate-700 tabular-nums">{formatNum(stats.total_in_flight)}</p>
        </div>
        <div className="card p-3">
          <p className="text-[10px] uppercase tracking-wider font-medium text-slate-400">Flagged</p>
          <p className="text-xl font-bold text-amber-600 tabular-nums">{formatNum(stats.flagged_count)}</p>
        </div>
        <div className="card p-3">
          <p className="text-[10px] uppercase tracking-wider font-medium text-slate-400">Severe</p>
          <p className="text-xl font-bold text-red-600 tabular-nums">{formatNum((stats.by_severity || {}).severe || 0)}</p>
        </div>
        <div className="card p-3">
          <p className="text-[10px] uppercase tracking-wider font-medium text-slate-400">Moderate</p>
          <p className="text-xl font-bold text-orange-600 tabular-nums">{formatNum((stats.by_severity || {}).moderate || 0)}</p>
        </div>
        <div className="card p-3">
          <p className="text-[10px] uppercase tracking-wider font-medium text-slate-400">Lanes w/ baseline</p>
          <p className="text-xl font-bold text-slate-700 tabular-nums">{formatNum(stats.lanes_with_baseline)}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">overall p90 {stats.overall_p90_days != null ? `${stats.overall_p90_days.toFixed(1)}d` : '—'}</p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setIncludeIntl(v => !v)}
            className={[
              'inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded border transition-colors',
              includeIntl
                ? 'bg-blue-600 border-blue-600 text-white'
                : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300',
            ].join(' ')}
          >
            <Globe size={11} />
            Include international
          </button>
          {!includeIntl && (
            <span className="text-[11px] text-slate-400">
              International excluded — AP doesn&rsquo;t surface destination-carrier scans, so they&rsquo;d false-positive as overdue.
            </span>
          )}
        </div>
        <div className="inline-flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden bg-white text-xs">
            {[7, 30, 90].map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={[
                  'px-3 py-1.5 font-medium transition-colors',
                  d === days ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50',
                ].join(' ')}
              >
                {d}d
              </button>
            ))}
          </div>
          <button
            onClick={() => qc.invalidateQueries({ queryKey: ['tracking', 'at-risk'] })}
            disabled={isFetching}
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-blue-600 disabled:opacity-50"
          >
            {isFetching ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left  font-semibold">Severity</th>
                <th className="px-3 py-2 text-right font-semibold">Overdue</th>
                <th className="px-3 py-2 text-left  font-semibold">Order</th>
                <th className="px-3 py-2 text-left  font-semibold">Client</th>
                <th className="px-3 py-2 text-left  font-semibold">Carrier · service</th>
                <th className="px-3 py-2 text-left  font-semibold">Dest</th>
                <th className="px-3 py-2 text-right font-semibold">Lane p90</th>
                <th className="px-3 py-2 text-right font-semibold">Expected by</th>
                <th className="px-3 py-2 text-left  font-semibold">Last scan</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {flags.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-3 py-12 text-center text-slate-400">
                    <OctagonAlert size={20} className="mx-auto text-slate-300 mb-2" />
                    <p>Nothing flagged — every in-flight shipment is within its lane baseline.</p>
                  </td>
                </tr>
              ) : flags.map(f => (
                <tr
                  key={f.order_id}
                  onClick={() => setDrawerRow(f)}
                  className="hover:bg-red-50/30 cursor-pointer transition-colors"
                >
                  <td className="px-3 py-2"><SeverityPill severity={f.severity} /></td>
                  <td className={`px-3 py-2 text-right tabular-nums font-semibold ${
                    f.severity === 'severe' ? 'text-red-700' : f.severity === 'moderate' ? 'text-orange-700' : 'text-amber-700'
                  }`}>
                    {f.days_overdue}d
                  </td>
                  <td className="px-3 py-2 font-medium text-slate-700">{f.order_number || '—'}</td>
                  <td className="px-3 py-2 text-slate-600">{f.client_name || '—'}</td>
                  <td className="px-3 py-2 text-slate-600">
                    {f.carrier}
                    {f.service && <span className="text-[10px] text-slate-400 ml-1">· {f.service_class}</span>}
                  </td>
                  <td className="px-3 py-2 text-slate-600">{f.destination_state}</td>
                  <td className="px-3 py-2 text-right text-slate-600 tabular-nums">{f.lane_p90_days?.toFixed(1)}d</td>
                  <td className="px-3 py-2 text-right text-slate-600 tabular-nums">{f.expected_by}</td>
                  <td className="px-3 py-2 text-slate-600">
                    <div className="leading-tight">
                      <p>{timeAgo(f.last_scan_at)}</p>
                      {f.last_scan_location && <p className="text-[10px] text-slate-400">{f.last_scan_location}</p>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drawer reuses the existing component — flags' shape carries
          enough overlap with the standard row shape (order_id,
          client_name, carrier, tracking_number, last_scan_*) that
          the drawer just works. */}
      <OrderDetailDrawer row={drawerRow} onClose={() => setDrawerRow(null)} />
    </div>
  )
}
